import React, { useState } from "react";

const inputCls = "w-full bg-[#0A0B10] border border-[#1E2233] focus:border-[#9D4CDD] rounded px-2 py-1.5 text-white outline-none transition-colors";

/**
 * PasteFromSheets — reusable Google Sheets TSV import widget.
 *
 * Renders a toggle button that opens a TSV textarea. Parses pasted text by
 * splitting on \n then \t. Calls onApply(matrix) where matrix is string[][].
 *
 * Props:
 *   onApply(matrix): callback invoked with the parsed 2-D array.
 *   columns: array of [key, label, type] — used for the helper hint string.
 *   testId: testid prefix (e.g. "skater-paste").
 *   buttonLabel: optional label override.
 *   hint: optional override for the helper line under the textarea.
 */
export function PasteFromSheets({ onApply, columns = [], testId, buttonLabel, hint }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const apply = () => {
        const parsed = text
            .split(/\r?\n/)
            .map((line) => line.split("\t"))
            .filter((row) => row.some((cell) => String(cell).trim() !== ""));
        onApply(parsed);
        setText("");
        setOpen(false);
    };
    const hintLine = hint || (
        columns.length
            ? `Copy a block from Google Sheets (${columns.length} columns: ${columns.map(([, l]) => l).join(" / ")}) and paste here. Rows are matched in order.`
            : "Copy a block from Google Sheets and paste here. Rows are matched in order."
    );
    return (
        <div className="text-xs">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-[#9D4CDD] hover:underline"
                data-testid={`${testId}-toggle`}
            >
                {open ? "× Close paste box" : (buttonLabel || "Paste from Google Sheets")}
            </button>
            {open && (
                <div className="mt-2 p-3 bg-[#0A0B10] border border-[#1E2233] rounded">
                    <p className="text-[10px] text-[#94A3B8] mb-2">{hintLine}</p>
                    <textarea
                        rows={6}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="# Paste TSV here (Ctrl+V from Sheets)"
                        className={inputCls + " font-mono text-xs"}
                        data-testid={`${testId}-textarea`}
                    />
                    <div className="flex items-center gap-2 mt-2">
                        <button
                            type="button"
                            onClick={apply}
                            disabled={!text.trim()}
                            className="bg-[#9D4CDD] text-white px-3 py-1.5 rounded text-xs uppercase tracking-wider disabled:opacity-40"
                            data-testid={`${testId}-apply`}
                        >
                            Apply paste
                        </button>
                        <span className="text-[10px] text-[#64748B]">
                            {text.split(/\r?\n/).filter((l) => l.trim()).length} non-empty row(s) detected
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Convert a parsed TSV matrix into row objects matching a column schema.
 * Each column descriptor: [key, label, type] where type ∈ "num" | "text" | "bool".
 *
 * Used by Season Stats import + Player creation bulk-add.
 */
export function tsvToObjects(matrix, schema, baseRow = {}) {
    return matrix.map((row) => {
        const obj = { ...baseRow };
        schema.forEach(([key, , type], idx) => {
            const cell = row[idx];
            if (cell === undefined || cell === "") return;
            if (type === "num") {
                const n = parseFloat(String(cell).replace(/[^\d.\-]/g, ""));
                obj[key] = Number.isFinite(n) ? n : 0;
            } else if (type === "bool") {
                obj[key] = ["1", "true", "y", "yes", "x", "✓"].includes(String(cell).toLowerCase().trim());
            } else {
                obj[key] = String(cell).trim();
            }
        });
        return obj;
    });
}
